import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/nextjs'
import { ManageOrderParams, CreateOrderResponse, Order, Gateway } from '@/types/commerce'
import { useRecaptcha } from '@/hooks/utils/useRecaptcha'
import { ORDER_STATUSES } from '@/constants/commerce'
import { getErrorMessage } from '@/types/error'
import { NO_USER_ERROR_CODE } from '@/constants/session'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { useUser } from '../users/useUser'

import settings from '@/settings/public'

/**
 * Thrown when the session resolved to no user. Kept distinct so the query
 * still fails — leaving `orders` undefined, which is what keeps banner
 * selection fail-closed — without reporting an ordinary session expiry.
 * Resolving to an empty list instead would read as "this user holds nothing"
 * and show non-subscriber banners to a subscriber during the refresh race.
 */
class NoUserSessionError extends Error {}

// The failure is cached, so every later mount of any observer on the key sees
// the same error object again (back-navigation within gcTime, StrictMode's
// double effect). Keyed on the error instance, which React Query keeps stable
// per failure.
const reportedErrors = new WeakSet<object>()

const ACTIVE_ORDER_STATUSES: number[] = [
  ORDER_STATUSES.PAUSED,
  ORDER_STATUSES.ACTIVE,
  ORDER_STATUSES.TRIAL,
  ORDER_STATUSES.PENDING
]
interface Filter {
  [key: string]: string | number | (string | number)[]
}

export const buildRequestParams = (args: ManageOrderParams) => {
  const { event, order, params } = args
  const gateway = (order?.gateway) as Gateway | undefined

  const defaultData: Record<string, any> = {
    ...(order?._id && { orderId: order._id }),
    ...(gateway?.type && { gateway: { type: gateway.type } })
  }

  const result = (data: Record<string, any>) => ({ event, data })

  if (event === 'pause') {
    return result({
      ...defaultData,
      // 2 ciclos = pausa de 60 días prometida en la UI; con 1, Recurly
      // auto-reanuda y cobra un mes antes (incidente jul-2026)
      remainingPauseCycles: params?.remainingPauseCycles ?? 2
    })
  }

  if (event === 'change') {
    return result({
      ...defaultData,
      timeframe: params?.timeframe ?? 'now',
      ...(params?.coupon && { coupon: params?.coupon }),
      ...(params?.productId && { productId: params?.productId }),
      ...(params?.sku && { productSKU: params?.sku }),
      ...(params?.isRetentionCoupon && { isRetentionCoupon: true })
    })
  }

  if (event === 'cancel') {
    return result({
      ...defaultData,
      ...(params?.cancelReason && { cancelReason: params.cancelReason }),
      ...(params?.cancelReasonDetail && { cancelReasonDetail: params.cancelReasonDetail })
    })
  }

  return result(defaultData)
}

const fetchOrders = async (filterBy: Filter = {}): Promise<Order[]> => {
  const url = new URL(`${settings.api.host}/api/1.0/commerce/orders`)

  const response = await fetch(url.toString())

  // A gateway error page (ALB/CloudFront HTML on 502/504) is not JSON, and the
  // status is the only useful thing left to report.
  let ordersJson
  try {
    ordersJson = await response.json()
  } catch {
    throw new Error(`Orders request returned ${response.status} ${response.statusText}`)
  }

  if (ordersJson.error) {
    if (String(ordersJson.error.code) === NO_USER_ERROR_CODE) {
      throw new NoUserSessionError('Orders request answered without a user session')
    }

    // getErrorMessage, not the raw value: the BFF sets userMessage as an
    // object, which `new Error(ordersJson.error)` turned into "[object Object]".
    throw new Error(getErrorMessage(ordersJson.error))
  }

  // Without this, a non-ok body carrying no `error` field reaches
  // `ordersJson.data.items` below and throws an unactionable TypeError.
  if (!response.ok) {
    throw new Error(`Orders request returned ${response.status} ${response.statusText}`)
  }

  let orders = ordersJson.data.items

  if (Object.keys(filterBy).length > 0) {
    orders = orders.filter((order: Order) => {
      return Object.keys(filterBy).every((key) => {
        const filterValue = filterBy[key]

        if (Array.isArray(filterValue)) {
          return filterValue.includes(order[key as keyof Order])
        }

        return order[key as keyof Order] === filterValue
      })
    })
  }

  return orders
}

const manageOrder = async ({ recaptchaToken, params }: {
  recaptchaToken: string
  params: ManageOrderParams
}): Promise<CreateOrderResponse> => {
  const url = `${settings.api.host}/api/commerce/orders`
  const requestBody = buildRequestParams(params)

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...requestBody, recaptchaToken }),
  })

  const responseJson = await response.json()

  if (responseJson.error) {
    throw responseJson.error
  }

  return responseJson
}

export const useOrders = (props?: {
  filterBy?: Filter
  enabled?: boolean
  /**
   * Left to React Query's own default (staleTime-governed) so the observers
   * that merely read the list — the banner strip, the modal triggers, the
   * delete-account check — stop forcing a fetch on every navigation. Screens
   * that must not act on a stale list pass 'always'; that choice is per
   * observer, but one 'always' observer refetches the shared key for all of
   * them, which is why only the screens that need it opt in. `false` is not
   * the same as the default: that observer would never refetch on mount at
   * all, so a change made elsewhere would never reach it.
   */
  refetchOnMount?: boolean | 'always'
}) => {
  const { filterBy = {}, enabled = true, refetchOnMount } = props || {}
  const queryClient = useQueryClient()
  const { withRecaptchaRetry } = useRecaptcha()
  const { profile } = useUser()

  const manageOrderMutation = useMutation({
    mutationFn: async (params: ManageOrderParams) => {
      return withRecaptchaRetry('manage_order', (recaptchaToken) => (
        manageOrder({ params: { ...params, profile }, recaptchaToken })
      ))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['orders'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['getCoupon'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['previewRenewal'],
      })
    },
  })

  const ordersQuery = useQuery({
    queryKey: [...QUERY_KEYS.ordersAll, filterBy],
    queryFn: () => fetchOrders(filterBy),
    enabled: enabled,
    refetchOnMount
  })

  const { error } = ordersQuery
  // Reported from here rather than from fetchOrders: the browser QueryClient
  // overrides only staleTime, so React Query's default retry runs the query
  // function four times per failed fetch, across several keys — capturing
  // inside it would multiply one outage into a dozen events.
  useEffect(() => {
    if (!error || error instanceof NoUserSessionError || reportedErrors.has(error)) return

    reportedErrors.add(error)
    Sentry.withScope((scope) => {
      scope.setLevel('warning')
      // Keeps the event past shouldDropEvent's manual-capture keep rule
      // (config/sentry-event-filter.ts).
      scope.setTag('manual_capture', 'true')
      scope.setExtra('manual_capture', true)
      // Rethrowing the status text or a raw network rejection would carry
      // "Not Found" / "Failed to fetch" / "Load failed", each of which
      // ignoreErrors drops before beforeSend ever runs — silencing the exact
      // outage this report exists for. Our own message clears that list; the
      // original survives as the cause.
      Sentry.captureException(new Error('Orders request failed', { cause: error }))
    })
  }, [error])

  return {
    ...ordersQuery,
    orders: ordersQuery.data,
    hasActiveOrders: ordersQuery.data?.some(order =>
      ACTIVE_ORDER_STATUSES.includes(order?.status) &&
      order?.type === 'renewable'
    ),
    manageOrder: manageOrderMutation.mutateAsync,
    isPendingManageOrder: manageOrderMutation.isPending,
    isSuccessManageOrder: manageOrderMutation.isSuccess,
    isErrorManageOrder: manageOrderMutation.isError,
    manageOrderMutation,
  }
}